/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { Flex } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type { ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import type { MessageActionInvocationStatus } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import { messages } from './messages';

const disabledActions: (MessageActionInvocationStatus | undefined)[] = [
	'finished',
	'cancelled',
	'errored',
];

export const StudioLandingPageRedirect = (
	props: ActionRendererFor<typeof ActionKeys.StudioLandingPageRedirect>,
): JSX.Element => {
	di();

	const { formatMessage } = useIntl();

	const {
		actions,
		onCancel,
		getStatus,
		onCustomDataConfirm,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
	} = props;

	const publish = usePublish('ai-mate');

	const firstAction = actions[0];
	const { data } = firstAction;

	const status = getStatus(firstAction);

	const isDisabled = status !== 'idle' || disabledActions.includes(firstAction.status);
	const isSubmitting = status === 'started';

	const onClick = async () => {
		if (
			FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');

			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: 'studioLandingPageRedirectRenderer',
				},
				confirmed_tools: {
					message_id: messageId ?? '',
					tools: idleActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed', data)
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
		} else {
			await onCustomDataConfirm(firstAction, data);
		}

		const { ari, prompt } = data;

		publish({
			type: 'studio-landing-page-redirect',
			data: {
				ari,
				prompt,
			},
			source: 'studio-landing-page-redirect-action-ui',
		});
	};

	return (
		<Flex
			testId="conversation-assistant-message-actions.studio-landing-page-redirect"
			gap="space.100"
		>
			<Button onClick={onClick} isDisabled={isDisabled} isLoading={isSubmitting}>
				{formatMessage(messages.confirmButton)}
			</Button>
			<Button
				appearance="subtle"
				onClick={() => onCancel(firstAction)}
				isDisabled={isDisabled}
				isLoading={isSubmitting}
			>
				{formatMessage(messages.cancelButton)}
			</Button>
		</Flex>
	);
};
